import {omitNullsy, serializeJSONToString, useApiQuery, useCurrentLocale} from '@wallaby-ui/core';
import {useMemo} from 'react';

import {
  InterventionsDepartmentReportBasicMeasuresResultDto,
  InterventionsDepartmentReportCategoryStatisticsDrpCause,
  InterventionsDepartmentReportCategoryStatisticsDrpPlannedIntervention,
  InterventionsDepartmentReportCategoryStatisticsDrpType,
  InterventionsDepartmentReportCategoryStatisticsLong,
  InterventionsDepartmentReportCategoryStatisticsRiskiness,
  InterventionsDepartmentReportDto,
  InterventionsDepartmentReportFilter,
} from './departmenttypes';

type UseReportDataProps = {
  reportUrl: string;
  filters: InterventionsDepartmentReportFilter;
};

type UseReportDataReturn = {
  reportData: InterventionsDepartmentReportDto | undefined;
  isLoading: boolean;
};

export const useDepartmentReportData = ({reportUrl, filters}: UseReportDataProps): UseReportDataReturn => {
  const locale = useCurrentLocale();

  const areAllFiltersSet = useMemo(() => {
    return filters.dateFrom != null && filters.dateTo != null && filters.measureType != null && filters.causesAggregationType != null;
  }, [filters]);

  const reportParams: InterventionsDepartmentReportFilter = useMemo(
    () =>
      omitNullsy({
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo,
        measureType: filters.measureType,
        organizationIds: filters.organizationIds,
        atcHierarchyIds: filters.atcHierarchyIds,
        riskCategories: filters.riskCategories,
        types: filters.types,
        manifestations: filters.manifestations,
        causes: filters.causes,
        causesAggregationType: filters.causesAggregationType,
        plannedInterventions: filters.plannedInterventions,
        language: locale.toUpperCase(),
      }),
    [filters, locale]
  );

  const {data: reportData, isLoading} = useApiQuery<InterventionsDepartmentReportDto>(
    [reportUrl, serializeJSONToString(reportParams)],
    null,
    {
      axiosConfig: {
        url: reportUrl,
        params: new URLSearchParams(reportParams as any),
      },
      reactQueryOptions: {
        enabled: areAllFiltersSet,
      },
    }
  );

  // const reportData = useTestData();

  return {
    reportData,
    isLoading,
  };
};

const useTestData = () => {
  const basicMeasures: InterventionsDepartmentReportBasicMeasuresResultDto = useMemo(
    () =>
      ({
        hospCount: 200.55,
        hospDaysCount: 200.55,
        uncheckedHospCount: 200.55,
        uncheckedDaysCount: 200.55,
        uncheckedHighRiskHospCount: 200.55,
        avgHospDuration: 200.55,
        avgDaysCount: 200.55,
        avgHospDurationDrp: 200.55,
        avgHospDurationNoDrp: 200.55,
        acceptanceRate: 88.55,
      } as InterventionsDepartmentReportBasicMeasuresResultDto),
    []
  );

  const riskinessStatistics: InterventionsDepartmentReportCategoryStatisticsRiskiness = useMemo(
    () =>
      ({
        charts: [
          {
            category: 'LOW',
            month: '2024/08',
            measureCount: 22.88,
            measureCountPer1000HospDays: 33.88,
          },
          {
            category: 'LOW',
            month: '2024/10',
            measureCount: 10.88,
            measureCountPer1000HospDays: 11.88,
          },
          {
            category: 'LOW',
            month: '2024/09',
            measureCount: 5.88,
            measureCountPer1000HospDays: 8.88,
          },
          {
            category: 'LOW',
            month: '2024/12',
            measureCount: 22.88,
            measureCountPer1000HospDays: 33.88,
          },
          {
            category: 'LOW',
            month: '2024/11',
            measureCount: 3.88,
            measureCountPer1000HospDays: 13.88,
          },
          {
            category: 'MEDIUM',
            month: '2024/08',
            measureCount: 3.88,
            measureCountPer1000HospDays: 33.88,
          },
          {
            category: 'MEDIUM',
            month: '2024/10',
            measureCount: 50.88,
            measureCountPer1000HospDays: 11.88,
          },
          {
            category: 'MEDIUM',
            month: '2024/09',
            measureCount: 25.88,
            measureCountPer1000HospDays: 8.88,
          },
          {
            category: 'MEDIUM',
            month: '2024/12',
            measureCount: 7.88,
            measureCountPer1000HospDays: 33.88,
          },
          {
            category: 'MEDIUM',
            month: '2024/11',
            measureCount: 18.88,
            measureCountPer1000HospDays: 13.88,
          },
          {
            category: 'HIGH',
            month: '2024/08',
            measureCount: 38.88,
            measureCountPer1000HospDays: 33.88,
          },
          {
            category: 'HIGH',
            month: '2024/10',
            measureCount: 22.88,
            measureCountPer1000HospDays: 11.88,
          },
          {
            category: 'HIGH',
            month: '2024/09',
            measureCount: 53.88,
            measureCountPer1000HospDays: 8.88,
          },
          {
            category: 'HIGH',
            month: '2024/12',
            measureCount: 39.88,
            measureCountPer1000HospDays: 33.88,
          },
          {
            category: 'HIGH',
            month: '2024/11',
            measureCount: 1.88,
            measureCountPer1000HospDays: 13.88,
          },
        ],
        tableTotal: {
          category: undefined,
          measureCount: 44.55,
          measureCountToHospCountPct: 55.88,
          measureCountPer1000HospDays: 66.55,
        },
        tableRows: [
          {
            category: 'HIGH',
            measureCount: 70.55,
            measureCountToHospCountPct: 88.88,
            measureCountPer1000HospDays: 200.55,
          },
          {
            category: 'MEDIUM',
            measureCount: 200.55,
            measureCountToHospCountPct: 22.88,
            measureCountPer1000HospDays: 11.55,
          },
          {
            category: 'LOW',
            measureCount: 30.55,
            measureCountToHospCountPct: 33.88,
            measureCountPer1000HospDays: 88.55,
          },
        ],
      } as InterventionsDepartmentReportCategoryStatisticsRiskiness),
    []
  );

  const causeStatistics: InterventionsDepartmentReportCategoryStatisticsDrpCause = useMemo(
    () => ({
      charts: [
        {
          category: 'DOSAGE_REGIMEN_NOT_FREQUENT_ENOUGH',
          month: '2024/08',
          measureCount: 22.88,
          measureCountPer1000HospDays: 33.88,
        },
        {
          category: 'DOSAGE_REGIMEN_NOT_FREQUENT_ENOUGH',
          month: '2024/10',
          measureCount: 10.88,
          measureCountPer1000HospDays: 11.88,
        },
        {
          category: 'CONTRAINDICATION',
          month: '2024/08',
          measureCount: 22.88,
          measureCountPer1000HospDays: 33.88,
        },
        {
          category: 'CONTRAINDICATION',
          month: '2024/10',
          measureCount: 10.88,
          measureCountPer1000HospDays: 11.88,
        },
        {
          category: 'DRUG_DOSE_TOO_LOW',
          month: '2024/08',
          measureCount: 22.88,
          measureCountPer1000HospDays: 33.88,
        },
        {
          category: 'DRUG_DOSE_TOO_LOW',
          month: '2024/10',
          measureCount: 10.88,
          measureCountPer1000HospDays: 11.88,
        },
        {
          category: 'DRUG_NOT_ADMINISTERED_BY_PROFESSIONAL',
          month: '2024/08',
          measureCount: 22.88,
          measureCountPer1000HospDays: 33.88,
        },
        {
          category: 'DRUG_NOT_ADMINISTERED_BY_PROFESSIONAL',
          month: '2024/10',
          measureCount: 10.88,
          measureCountPer1000HospDays: 11.88,
        },
        {
          category: 'GROUP_DRUG_FORM',
          month: '2024/08',
          measureCount: 22.88,
          measureCountPer1000HospDays: 33.88,
        },
        {
          category: 'GROUP_DRUG_FORM',
          month: '2024/10',
          measureCount: 10.88,
          measureCountPer1000HospDays: 11.88,
        },
        {
          category: undefined,
          month: '2024/08',
          measureCount: 22.88,
          measureCountPer1000HospDays: 33.88,
        },
        {
          category: undefined,
          month: '2024/10',
          measureCount: 10.88,
          measureCountPer1000HospDays: 11.88,
        },
      ],
      tableTotal: {
        category: undefined,
        measureCount: 200.88,
        measureCountToHospCountPct: 88.88,
        measureCountPer1000HospDays: 200.55,
      },
      tableRows: [
        {
          category: 'DOSAGE_REGIMEN_NOT_FREQUENT_ENOUGH',
          measureCount: 200.55,
          measureCountToHospCountPct: 88.88,
          measureCountPer1000HospDays: 200.55,
        },
        {
          category: 'DRUG_ADMINISTERED_BY_WRONG_ROUTE_BY_PROFESSIONAL',
          measureCount: 200.55,
          measureCountToHospCountPct: 88.88,
          measureCountPer1000HospDays: 200.55,
        },
      ],
    }),
    []
  );

  const typeOfProblemStatistics: InterventionsDepartmentReportCategoryStatisticsDrpType = useMemo(
    () => ({
      charts: [
        {
          category: 'NO_EFFECT_DESPITE_CORRECT_USE',
          month: '2024/08',
          measureCount: 22.88,
          measureCountPer1000HospDays: 33.88,
        },
        {
          category: 'NO_EFFECT_DESPITE_CORRECT_USE',
          month: '2024/10',
          measureCount: 10.88,
          measureCountPer1000HospDays: 11.88,
        },
        {
          category: 'NO_EFFECT_DESPITE_CORRECT_USE',
          month: '2024/09',
          measureCount: 5.88,
          measureCountPer1000HospDays: 8.88,
        },
        {
          category: 'NO_EFFECT_DESPITE_CORRECT_USE',
          month: '2024/12',
          measureCount: 22.88,
          measureCountPer1000HospDays: 33.88,
        },
        {
          category: 'NO_EFFECT_DESPITE_CORRECT_USE',
          month: '2024/11',
          measureCount: 3.88,
          measureCountPer1000HospDays: 13.88,
        },
        {
          category: 'UNCLEAR_PROBLEM',
          month: '2024/08',
          measureCount: 3.88,
          measureCountPer1000HospDays: 33.88,
        },
        {
          category: 'UNCLEAR_PROBLEM',
          month: '2024/10',
          measureCount: 50.88,
          measureCountPer1000HospDays: 11.88,
        },
        {
          category: 'UNCLEAR_PROBLEM',
          month: '2024/09',
          measureCount: 25.88,
          measureCountPer1000HospDays: 8.88,
        },
        {
          category: 'UNCLEAR_PROBLEM',
          month: '2024/12',
          measureCount: 7.88,
          measureCountPer1000HospDays: 33.88,
        },
        {
          category: 'UNCLEAR_PROBLEM',
          month: '2024/11',
          measureCount: 18.88,
          measureCountPer1000HospDays: 13.88,
        },
      ],
      tableTotal: {
        category: undefined,
        measureCount: 200.88,
        measureCountToHospCountPct: 88.88,
        measureCountPer1000HospDays: 200.55,
      },
      tableRows: [
        {
          category: 'NO_EFFECT_DESPITE_CORRECT_USE',
          measureCount: 200.55,
          measureCountToHospCountPct: 88.88,
          measureCountPer1000HospDays: 200.55,
        },
        {
          category: 'UNCLEAR_PROBLEM',
          measureCount: 200.55,
          measureCountToHospCountPct: 88.88,
          measureCountPer1000HospDays: 200.55,
        },
      ],
    }),
    []
  );

  const plannedInterventionStatistics: InterventionsDepartmentReportCategoryStatisticsDrpPlannedIntervention = useMemo(
    () => ({
      charts: [
        {
          category: 'NO_INTERVENTION',
          month: '2024/08',
          measureCount: 22.88,
          measureCountPer1000HospDays: 33.88,
        },
        {
          category: 'NO_INTERVENTION',
          month: '2024/10',
          measureCount: 10.88,
          measureCountPer1000HospDays: 11.88,
        },
        {
          category: 'DRUG_CHANGED_TO',
          month: '2024/08',
          measureCount: 22.88,
          measureCountPer1000HospDays: 33.88,
        },
        {
          category: 'DRUG_CHANGED_TO',
          month: '2024/10',
          measureCount: 10.88,
          measureCountPer1000HospDays: 11.88,
        },
        {
          category: 'DOSAGE_CHANGED_TO',
          month: '2024/08',
          measureCount: 22.88,
          measureCountPer1000HospDays: 33.88,
        },
        {
          category: 'DOSAGE_CHANGED_TO',
          month: '2024/10',
          measureCount: 10.88,
          measureCountPer1000HospDays: 11.88,
        },
        {
          category: 'FORMULATION_CHANGED_TO',
          month: '2024/08',
          measureCount: 22.88,
          measureCountPer1000HospDays: 33.88,
        },
        {
          category: 'FORMULATION_CHANGED_TO',
          month: '2024/10',
          measureCount: 10.88,
          measureCountPer1000HospDays: 11.88,
        },
        {
          category: 'INSTRUCTION_FOR_USE_CHANGED_TO',
          month: '2024/08',
          measureCount: 22.88,
          measureCountPer1000HospDays: 33.88,
        },
        {
          category: 'INSTRUCTION_FOR_USE_CHANGED_TO',
          month: '2024/10',
          measureCount: 10.88,
          measureCountPer1000HospDays: 11.88,
        },
        {
          category: undefined,
          month: '2024/08',
          measureCount: 22.88,
          measureCountPer1000HospDays: 33.88,
        },
        {
          category: undefined,
          month: '2024/10',
          measureCount: 10.88,
          measureCountPer1000HospDays: 11.88,
        },
      ],
      tableTotal: {
        category: undefined,
        measureCount: 200.88,
        measureCountToHospCountPct: 88.88,
        measureCountPer1000HospDays: 200.55,
      },
      tableRows: [
        {
          category: 'FORMULATION_CHANGED_TO',
          measureCount: 200.55,
          measureCountToHospCountPct: 88.88,
          measureCountPer1000HospDays: 200.55,
        },
        {
          category: 'INSTRUCTION_FOR_USE_CHANGED_TO',
          measureCount: 200.55,
          measureCountToHospCountPct: 88.88,
          measureCountPer1000HospDays: 200.55,
        },
        {
          category: 'DOSAGE_CHANGED_TO',
          measureCount: 200.55,
          measureCountToHospCountPct: 88.88,
          measureCountPer1000HospDays: 200.55,
        },
        {
          category: 'NO_INTERVENTION',
          measureCount: 200.55,
          measureCountToHospCountPct: 88.88,
          measureCountPer1000HospDays: 200.55,
        },
        {
          category: 'DRUG_STARTED',
          measureCount: 200.55,
          measureCountToHospCountPct: 88.88,
          measureCountPer1000HospDays: 200.55,
        },
        {
          category: 'DRUG_PAUSED_OR_STOPPED',
          measureCount: 200.55,
          measureCountToHospCountPct: 88.88,
          measureCountPer1000HospDays: 200.55,
        },
        {
          category: 'OTHER_INTERVENTION',
          measureCount: 200.55,
          measureCountToHospCountPct: 88.88,
          measureCountPer1000HospDays: 200.55,
        },
      ],
    }),
    []
  );

  const atcHierarchyStatistics: InterventionsDepartmentReportCategoryStatisticsLong = useMemo(
    () => ({
      charts: [
        {
          category: 112474,
          month: '2024/08',
          measureCount: 22.88,
          measureCountPer1000HospDays: 33.88,
        },
        {
          category: 112474,
          month: '2024/10',
          measureCount: 10.88,
          measureCountPer1000HospDays: 11.88,
        },
        {
          category: 112475,
          month: '2024/08',
          measureCount: 22.88,
          measureCountPer1000HospDays: 33.88,
        },
        {
          category: 112475,
          month: '2024/10',
          measureCount: 10.88,
          measureCountPer1000HospDays: 11.88,
        },
        {
          category: 112476,
          month: '2024/08',
          measureCount: 22.88,
          measureCountPer1000HospDays: 33.88,
        },
        {
          category: 112476,
          month: '2024/10',
          measureCount: 10.88,
          measureCountPer1000HospDays: 11.88,
        },
        {
          category: 112477,
          month: '2024/08',
          measureCount: 22.88,
          measureCountPer1000HospDays: 33.88,
        },
        {
          category: 112477,
          month: '2024/10',
          measureCount: 10.88,
          measureCountPer1000HospDays: 11.88,
        },
        {
          category: 112478,
          month: '2024/08',
          measureCount: 22.88,
          measureCountPer1000HospDays: 33.88,
        },
        {
          category: 112478,
          month: '2024/10',
          measureCount: 10.88,
          measureCountPer1000HospDays: 11.88,
        },
        {
          category: undefined,
          month: '2024/08',
          measureCount: 22.88,
          measureCountPer1000HospDays: 33.88,
        },
        {
          category: undefined,
          month: '2024/10',
          measureCount: 10.88,
          measureCountPer1000HospDays: 11.88,
        },
      ],
      tableTotal: {
        category: undefined,
        measureCount: 200.88,
        measureCountToHospCountPct: 88.88,
        measureCountPer1000HospDays: 200.55,
      },
      tableRows: [
        {
          category: 112474,
          measureCount: 200.55,
          measureCountToHospCountPct: 88.88,
          measureCountPer1000HospDays: 200.55,
        },
        {
          category: 112475,
          measureCount: 200.55,
          measureCountToHospCountPct: 88.88,
          measureCountPer1000HospDays: 200.55,
        },
        {
          category: 112475,
          measureCount: 200.55,
          measureCountToHospCountPct: 88.88,
          measureCountPer1000HospDays: 200.55,
        },
        {
          category: 112476,
          measureCount: 200.55,
          measureCountToHospCountPct: 88.88,
          measureCountPer1000HospDays: 200.55,
        },
        {
          category: 112477,
          measureCount: 200.55,
          measureCountToHospCountPct: 88.88,
          measureCountPer1000HospDays: 200.55,
        },
        {
          category: 112478,
          measureCount: 200.55,
          measureCountToHospCountPct: 88.88,
          measureCountPer1000HospDays: 200.55,
        },
        {
          category: 112479,
          measureCount: 200.55,
          measureCountToHospCountPct: 88.88,
          measureCountPer1000HospDays: 200.55,
        },
        {
          category: 112480,
          measureCount: 200.55,
          measureCountToHospCountPct: 88.88,
          measureCountPer1000HospDays: 200.55,
        },
        {
          category: 112481,
          measureCount: 200.55,
          measureCountToHospCountPct: 88.88,
          measureCountPer1000HospDays: 200.55,
        },
        {
          category: 112482,
          measureCount: 200.55,
          measureCountToHospCountPct: 88.88,
          measureCountPer1000HospDays: 200.55,
        },
        {
          category: 112483,
          measureCount: 200.55,
          measureCountToHospCountPct: 88.88,
          measureCountPer1000HospDays: 200.55,
        },
        {
          category: 112484,
          measureCount: 200.55,
          measureCountToHospCountPct: 88.88,
          measureCountPer1000HospDays: 200.55,
        },
        {
          category: 112485,
          measureCount: 200.55,
          measureCountToHospCountPct: 88.88,
          measureCountPer1000HospDays: 200.55,
        },
        {
          category: 112486,
          measureCount: 200.55,
          measureCountToHospCountPct: 88.88,
          measureCountPer1000HospDays: 200.55,
        },
        {
          category: 112487,
          measureCount: 200.55,
          measureCountToHospCountPct: 88.88,
          measureCountPer1000HospDays: 200.55,
        },
      ],
    }),
    []
  );

  return useMemo(() => {
    return {
      basicMeasures,
      riskinessStatistics,
      atcHierarchyStatistics,
      typeOfProblemStatistics,
      causeStatistics,
      plannedInterventionStatistics,
    };
  }, [basicMeasures, riskinessStatistics, atcHierarchyStatistics, typeOfProblemStatistics, causeStatistics, plannedInterventionStatistics]);
};
